import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
	DocumentArrowDownIcon,
	FunnelIcon,
	UserGroupIcon,
	ArrowsUpDownIcon,
} from '@heroicons/react/20/solid';

import { cleanActionSelectedPreRegister, getAllPreRegister } from '../../redux/actions/preRegistration';
import { verifyStateApp } from '../../redux/actions/stateApp';
import { exportToExcel } from '../../common/importDataToExcel';
import { getOrderedExcelHeadersForExport } from '../../common/getOrderedExcelHeadersForExport';

import { ContainerFull } from '../../components/ContainerFull';
import { Heading } from '../../components/Heading';
import { Wrapper } from '../../components/Wrapper';
import { Button } from '../../components/buttons/Button';
import { PopoverCheckboxFilter } from '../../components/popoverCheckboxFilter/PopoverCheckboxFilter';
import { PopoverRadioFilter } from '../../components/popoverRadioFilter/PopoverRadioFilter';
import { optionsStatusCandidate, optionsSort } from '../../static/data';
import { PopoverRangeDate } from '../../components/popoverRangeDate/PopoverRangeDate';
import { SmartTable } from '../../components/smartTable/SmartTable';
import { Tooltip } from '../../components/smartTable/Tooltip';
import { useTablePrefs } from '../../hooks/useTablePrefs';

import { DrawerCandidateValidation } from './DrawerCandidateValidation';
import { DrawerFiltersCandidates } from './DrawerFiltersCandidates';
import { buildCandidatesColumns } from './candidates.columns';

const TABLE_KEY = 'candidatos';

const EMPTY_FILTERS = {
	status: [],
	startDate: '',
	endDate: '',
};

const DEFAULT_PREFS = {
	query: {
		page: 0,
		pageSize: 15,
		sorting: [{ id: 'createdAt', desc: true }],
		filters: { ...EMPTY_FILTERS },
		columnSearch: {},
	},
	view: {
		columnVisibility: {},
		columnOrder: [],
		columnSizing: {},
	},
};

const CandidatesFilterBarSkeleton = () => (
	<div className='hidden w-full flex-wrap items-center justify-end gap-3 md:flex md:flex-1'>
		{[1, 2, 3].map((i) => (
			<div
				key={i}
				className='h-10 w-44 max-w-[40vw] shrink-0 rounded-md bg-slate-200/90 animate-blink'
				aria-hidden
			/>
		))}
	</div>
);

const sortingToApi = (sorting) => {
	if (!Array.isArray(sorting) || sorting.length === 0) {
		return { field: 'createdAt', dir: 'desc' };
	}
	const [first] = sorting;
	return { field: first.id, dir: first.desc ? 'desc' : 'asc' };
};

export const CandidatesPage = () => {
	const dispatch = useDispatch();
	const { allPreRegisters, loading } = useSelector((state) => state.preRegistration);
	const { user } = useSelector((state) => state.user);
	const { _id: id, roles, typeUser } = user;

	const typeUserCanValidate = ['coordinador', 'director', 'ventas'];
	const typeUserCanView = ['cobranza', 'subdirector', 'control escolar'];

	const [prefs, , patchPrefs, resetPrefs, prefsHydrated] = useTablePrefs(TABLE_KEY, DEFAULT_PREFS);
	const [totalRows, setTotalRows] = useState(0);
	const [idPreRegister, setIdPreRegister] = useState('');
	const [isOpenDrawerCandidateValidation, setIsOpenDrawerCandidateValidation] = useState(false);
	const [isOpenFiltersDrawer, setIsOpenFiltersDrawer] = useState(false);
	const [exportExcelLoading, setExportExcelLoading] = useState(false);

	const [formData, setFormData] = useState(() => ({ ...EMPTY_FILTERS, ...prefs.query.filters }));
	const [mobileFormData, setMobileFormData] = useState(formData);
	const [mobileSortDesc, setMobileSortDesc] = useState(() => prefs.query.sorting?.[0]?.desc ?? true);

	const filtersSyncKey = JSON.stringify(prefs.query.filters ?? {});
	useEffect(() => {
		setFormData({ ...EMPTY_FILTERS, ...prefs.query.filters });
	}, [filtersSyncKey]);

	const patchQuery = useCallback((patch) => {
		patchPrefs({ query: { ...prefs.query, ...patch } });
	}, [patchPrefs, prefs.query]);

	const patchView = useCallback((patch) => {
		patchPrefs({ view: { ...prefs.view, ...patch } });
	}, [patchPrefs, prefs.view]);

	const handleColumnSearchChange = useCallback((columnId, value) => {
		const current = prefs.query.columnSearch || {};
		const trimmed = (value || '').trim();
		const next = { ...current };
		if (trimmed) {
			if (current[columnId] === trimmed) return;
			next[columnId] = trimmed;
		} else {
			if (!(columnId in current)) return;
			delete next[columnId];
		}
		patchQuery({ page: 0, columnSearch: next });
	}, [patchQuery, prefs.query.columnSearch]);

	const buildApiPayload = useCallback((query) => ({
		id,
		roles,
		page: query.page,
		pageSize: query.pageSize,
		filters: query.filters || {},
		columnSearch: query.columnSearch || {},
		sort: sortingToApi(query.sorting),
		usePagination: true,
	}), [id, roles]);

	const refreshTable = useCallback(() => {
		return dispatch(getAllPreRegister(buildApiPayload(prefs.query))).then((result) => {
			if (result?.status === 200) {
				setTotalRows(result.totalRows || 0);
			}
		});
	}, [dispatch, buildApiPayload, prefs.query]);

	const handleOpenDrawerCandidateValidation = useCallback((preRegisterId) => {
		setIdPreRegister(preRegisterId);
		setIsOpenDrawerCandidateValidation(true);
	}, []);

	const toggleDrawerCandidateValidation = useCallback(() => {
		setIsOpenDrawerCandidateValidation((prev) => {
			if (prev) setIdPreRegister('');
			return !prev;
		});
	}, []);

	const columns = useMemo(
		() => buildCandidatesColumns({
			typeUser,
			typeUserCanValidate,
			typeUserCanView,
			onOpenValidation: handleOpenDrawerCandidateValidation,
		}),
		[typeUser, handleOpenDrawerCandidateValidation],
	);

	useEffect(() => {
		dispatch(cleanActionSelectedPreRegister());
	}, [dispatch]);

	const queryKey = JSON.stringify(prefs.query);
	useEffect(() => {
		if (!prefsHydrated) return;
		dispatch(getAllPreRegister(buildApiPayload(prefs.query))).then((result) => {
			if (result?.status === 200) {
				setTotalRows(result.totalRows || 0);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [queryKey, dispatch, prefsHydrated, id, roles]);

	const applyFilters = (nextFormData) => {
		const data = nextFormData || formData;
		patchQuery({
			page: 0,
			filters: {
				status: data.status || [],
				startDate: data.startDate || '',
				endDate: data.endDate || '',
			},
		});
	};

	const removeFilters = useCallback(() => {
		setFormData({ ...EMPTY_FILTERS });
		patchQuery({
			page: 0,
			filters: { ...EMPTY_FILTERS },
			columnSearch: {},
			sorting: [{ id: 'createdAt', desc: true }],
		});
	}, [patchQuery]);

	const currentSortValue = (prefs.query.sorting?.[0]?.desc ?? true) ? 'desc' : 'asc';
	const handleSortChange = useCallback(
		(value) => {
			patchQuery({
				page: 0,
				sorting: [{ id: 'createdAt', desc: value === 'desc' }],
			});
		},
		[patchQuery],
	);

	const toggleFiltersDrawer = useCallback(() => {
		setIsOpenFiltersDrawer((prev) => {
			const next = !prev;
			if (next) {
				setMobileFormData(formData);
				setMobileSortDesc(prefs.query.sorting?.[0]?.desc ?? true);
			}
			return next;
		});
	}, [formData, prefs.query.sorting]);

	const applyMobileFilters = useCallback(() => {
		setFormData(mobileFormData);
		patchQuery({
			page: 0,
			filters: {
				status: mobileFormData.status || [],
				startDate: mobileFormData.startDate || '',
				endDate: mobileFormData.endDate || '',
			},
			sorting: [{ id: 'createdAt', desc: mobileSortDesc }],
		});
		setIsOpenFiltersDrawer(false);
	}, [mobileFormData, mobileSortDesc, patchQuery]);

	const clearMobileFilters = useCallback(() => {
		setMobileFormData({ ...EMPTY_FILTERS });
		setMobileSortDesc(true);
		removeFilters();
	}, [removeFilters]);

	const handleImportData = async (name) => {
		const stateAppRes = await dispatch(verifyStateApp());
		if (stateAppRes?.data === false) {
			toast.error(stateAppRes.message);
			return;
		}

		const excelColumns = getOrderedExcelHeadersForExport(columns, prefs.view);
		if (excelColumns.length === 0) {
			toast.error('No hay columnas de datos para exportar. Muestra al menos una columna exportable en la tabla.');
			return;
		}

		setExportExcelLoading(true);
		const loadingToastId = toast.loading('Generando...');
		try {
			const result = await dispatch(getAllPreRegister({
				...buildApiPayload(prefs.query),
				usePagination: false,
				sendExcel: true,
				excelColumns,
			}));
			if (result?.status === 200) {
				const response = exportToExcel(result.excelData, name);
				if (response.data) {
					toast.error(response.message);
				} else {
					toast.success(response.message);
				}
			} else {
				toast.error('No se pudo generar el archivo Excel.');
			}
		} catch {
			toast.error('Error al generar el archivo Excel.');
		} finally {
			toast.dismiss(loadingToastId);
			setExportExcelLoading(false);
		}
	};

	const rows = allPreRegisters || [];

	return (
		<ContainerFull>
			<Heading
				title='Lista de candidatos'
				subtitle='Muestra la lista candidatos con su estatus de proceso'
				center={false}
			/>

			<div className='mt-4 flex w-full flex-col items-center justify-end gap-4 md:flex-row'>
				<div className='flex w-full md:hidden'>
					<Button
						onClick={toggleFiltersDrawer}
						disabled={!prefsHydrated}
						icon={<FunnelIcon className='block h-5 w-5' aria-hidden='true' />}
						variant='barFilterSecondary'
						className='w-full'
					>
						Filtros
					</Button>
				</div>

				{prefsHydrated ? (
					<>
						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip title='Orden por fecha de solicitud' placement='top'>
								<div className='w-full md:w-auto'>
									<PopoverRadioFilter
										value={currentSortValue}
										onChange={handleSortChange}
										options={optionsSort}
										title='Orden por fecha de solicitud'
										subtitle='Más reciente o más antiguo según la fecha en que se registró la solicitud.'
										icon={ArrowsUpDownIcon}
										placeholderLabel='Ordenar por'
									/>
								</div>
							</Tooltip>
						</div>

						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip title='Filtrar por rango de fechas' placement='top'>
								<div className='w-full md:w-auto'>
									<PopoverRangeDate
										state={formData}
										setState={setFormData}
										properties={['startDate', 'endDate']}
										popoverTitle='Fecha de solicitud'
										popoverSubtitle='Filtra candidatos por la fecha en que realizaron su solicitud.'
										emptySummaryLabel='Fecha de solicitud'
										handleApllyFilters={applyFilters}
										handleRemoveFilter={removeFilters}
									/>
								</div>
							</Tooltip>
						</div>

						<div className='hidden w-full md:flex md:w-auto'>
							<Tooltip title='Filtrar por estatus' placement='top'>
								<div className='w-full md:w-auto'>
									<PopoverCheckboxFilter
										state={formData}
										setState={setFormData}
										property='status'
										options={optionsStatusCandidate}
										title='Estatus del candidato'
										subtitle='Selecciona uno o varios estatus para filtrar los candidatos.'
										icon={UserGroupIcon}
										placeholderLabel='Estatus del candidato'
										handleApllyFilters={applyFilters}
										handleRemoveFilter={removeFilters}
									/>
								</div>
							</Tooltip>
						</div>
					</>
				) : (
					<CandidatesFilterBarSkeleton />
				)}

				<div className='flex w-full flex-col items-stretch gap-1 md:w-auto md:items-end'>
					<Tooltip
						title={exportExcelLoading ? 'Generando archivo Excel…' : 'Descargar archivo de Excel'}
						placement='top'
					>
						<Button
							onClick={() => handleImportData('Lista_de_candidatos.xlsx')}
							disabled={!prefsHydrated || exportExcelLoading}
							icon={<DocumentArrowDownIcon className='block h-5 w-5' aria-hidden='true' />}
							variant='barFilterSecondary'
							className='w-full md:w-auto'
							loading={exportExcelLoading}
							loadingLabel='Generando...'
						>
							Exportar lista
						</Button>
					</Tooltip>
				</div>
			</div>

			<Wrapper>
				<SmartTable
					data={rows}
					columns={columns}
					totalRows={totalRows}
					query={prefs.query}
					onQueryChange={patchQuery}
					onColumnSearchChange={handleColumnSearchChange}
					view={prefs.view}
					onViewChange={patchView}
					loading={loading}
					preferencesReady={prefsHydrated}
					emptyMessage='No hay registro de candidatos'
					pageSizeOptions={[10, 15, 25, 50]}
					onResetPrefs={resetPrefs}
					getRowId={(row) => row.id}
				/>
			</Wrapper>

			<DrawerCandidateValidation
				isOpenDrawer={isOpenDrawerCandidateValidation}
				toggleDrawer={toggleDrawerCandidateValidation}
				idPreRegister={idPreRegister}
				updateDataTable={refreshTable}
			/>

			<DrawerFiltersCandidates
				isOpen={isOpenFiltersDrawer}
				toggleDrawer={toggleFiltersDrawer}
				formData={mobileFormData}
				setFormData={setMobileFormData}
				sortValue={mobileSortDesc ? 'desc' : 'asc'}
				onSortChange={(value) => setMobileSortDesc(value === 'desc')}
				onClear={clearMobileFilters}
				onApply={applyMobileFilters}
			/>
		</ContainerFull>
	);
};
